import * as XLSX from 'xlsx';
import { ExcelData, FileConfig, CitySums, CityTerminal } from './types';
import { FILE_CONFIGS } from './constants';

/**
 * Извлекает код терминала из строки "Склад передачи КД" 
 * Например: "1119003_PP1" -> "PP1"
 */
export const extractTerminalIdFromWarehouse = (warehouseTransfer: string): string | null => {
    if (!warehouseTransfer || typeof warehouseTransfer !== 'string') return null;

    const parts = warehouseTransfer.toString().trim().split('_');
    if (parts.length >= 2) {
        const terminalId = parts[parts.length - 1]; // Берем последнюю часть после "_"
        return terminalId.trim();
    }

    return null;
};

/**
 * Находит город по terminal_id (например, "PP1", "PP7", "PP14")
 */
export const findCityByTerminalId = (
    terminalId: string,
    cityTerminals: CityTerminal[]
): CityTerminal | null => {
    if (!terminalId) return null;

    return cityTerminals.find(terminal =>
        terminal.terminal_id === terminalId
    ) || null;
};

/**
 * Улучшенная обработка адреса с приоритетом "Склад передачи КД"
 * Приоритет 1: Поиск по terminal_id из "Склад передачи КД"
 * Приоритет 2: Жесткое сравнение полного адреса с адресами из справочника
 * Возвращает: адрес, terminalId, cityName, createdBy (аптека), source
 */
export const processAddress = (
    address: string,
    warehouseTransfer: string | null | undefined,
    cityTerminals: CityTerminal[]
): {
    processedAddress: string;
    terminalId: string | null;
    cityName: string | null;
    createdBy: string | null;
    source: 'warehouse' | 'address' | 'none'
} => {

    // Приоритет 1: Проверяем "Склад передачи КД"
    if (warehouseTransfer) {
        const extractedTerminalId = extractTerminalIdFromWarehouse(warehouseTransfer);
        if (extractedTerminalId) {
            const cityTerminal = findCityByTerminalId(extractedTerminalId, cityTerminals);
            if (cityTerminal) {
                return {
                    processedAddress: address || '',
                    terminalId: cityTerminal.terminal_code,
                    cityName: cityTerminal.city_name,
                    createdBy: cityTerminal.created_by,
                    source: 'warehouse'
                };
            }
        }
    }

    // Приоритет 2: Жесткое сравнение полного адреса с адресами из справочника
    if (address) {
        const addressTrimmed = address.trim();

        for (const terminal of cityTerminals) {
            // Жесткое сравнение: точное совпадение адресов (регистронезависимое)
            if (terminal.address && terminal.address.trim().toLowerCase() === addressTrimmed.toLowerCase()) {
                return {
                    processedAddress: address,
                    terminalId: terminal.terminal_code,
                    cityName: terminal.city_name,
                    createdBy: terminal.created_by,
                    source: 'address'
                };
            }
        }

        // Если точного совпадения не найдено
        return {
            processedAddress: address,
            terminalId: null,
            cityName: null,
            createdBy: null,
            source: 'none'
        };
    }

    return {
        processedAddress: '',
        terminalId: null,
        cityName: null,
        createdBy: null,
        source: 'none'
    };
};

export const readExcelFile = (
    file: File,
    fileType: 'salesReport' | 'archiveOrders'
): Promise<ExcelData> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = e.target?.result;

                const workbook = XLSX.read(data, {
                    type: 'binary',
                    cellDates: true,
                    cellNF: false,
                    cellText: false
                });

                const result: ExcelData = {};
                const config = FILE_CONFIGS[fileType];

                workbook.SheetNames.forEach(sheetName => {
                    const worksheet = workbook.Sheets[sheetName];

                    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1,
                        blankrows: false,
                        defval: '',
                        raw: false
                    });

                    if (jsonData.length > config.headerRow) {
                        const headers = (jsonData[config.headerRow] as string[])
                            .map(header => header?.toString().trim() || '')
                            .filter(header => header !== '');

                        const rows = jsonData.slice(config.headerRow + 1);

                        result[sheetName] = {
                            headers,
                            data: rows.map(row => {
                                const rowData: { [key: string]: any } = {};
                                headers.forEach((header, index) => {
                                    let value = (row as any[])[index];
                                    rowData[header] = value !== undefined ? value : '';
                                });
                                return rowData;
                            })
                        };
                    }
                });

                resolve(result);
            } catch (error) {
                console.error('Detailed error reading file:', error);
                reject(error);
            }
        };

        reader.onerror = (error) => {
            console.error('FileReader error:', error);
            reject(error);
        };

        reader.readAsBinaryString(file);
    });
};

/**
 * ИСПРАВЛЕННАЯ функция расчета сумм с учетом новой логики определения городов
 * Теперь группировка идет по аптекам (created_by), а не по городам
 */
export const calculateSums = (
    salesData: ExcelData,
    archiveData: ExcelData | null,
    cityTerminals: CityTerminal[]
): CitySums & { unassignedSum: number } => {
    const firstSalesSheet = Object.values(salesData)[0];
    if (!firstSalesSheet) return { total: 0, unassignedSum: 0 };

    const newSums: CitySums & { unassignedSum: number } = {
        total: 0,
        unassignedSum: 0 // Сумма заказов без привязки к аптеке
    };

    // Инициализируем суммы для каждой аптеки (created_by)
    cityTerminals.forEach(terminal => {
        newSums[terminal.created_by] = 0;
    });

    // Создаем Map из ArchiveOrders если она есть
    let archiveOrdersMap: Map<string, { address: string; warehouseTransfer: string | undefined }> | null = null;

    if (archiveData) {
        const firstArchiveSheet = Object.values(archiveData)[0];
        if (firstArchiveSheet) {
            archiveOrdersMap = new Map(
                firstArchiveSheet.data.map(row => [
                    row[FILE_CONFIGS.archiveOrders.mappings.orderId]?.toString(),
                    {
                        address: row[FILE_CONFIGS.archiveOrders.mappings.address],
                        warehouseTransfer: FILE_CONFIGS.archiveOrders.mappings.warehouseTransfer
                            ? row[FILE_CONFIGS.archiveOrders.mappings.warehouseTransfer]
                            : undefined
                    }
                ])
            );
        }
    }

    // Обрабатываем каждую строку Sales Report
    firstSalesSheet.data.forEach(row => {
        const amount = parseFloat(row[FILE_CONFIGS.salesReport.mappings.amount!]) || 0;
        const orderId = row[FILE_CONFIGS.salesReport.mappings.orderId]?.toString();

        newSums.total += amount;

        // Определяем аптеку (created_by) по той же логике, что и в matchAndUpdateData
        let pharmacyAssigned = false;

        if (archiveOrdersMap && orderId) {
            const archiveData = archiveOrdersMap.get(orderId);
            if (archiveData) {
                const { createdBy } = processAddress(
                    archiveData.address,
                    archiveData.warehouseTransfer,
                    cityTerminals
                );

                if (createdBy) {
                    newSums[createdBy] = (newSums[createdBy] || 0) + amount;
                    pharmacyAssigned = true;
                }
            }
        }

        // Если не удалось определить аптеку через ArchiveOrders, пробуем по исходному адресу
        // ЖЕСТКОЕ СРАВНЕНИЕ полного адреса
        if (!pharmacyAssigned) {
            const address = row[FILE_CONFIGS.salesReport.mappings.address]?.toString().trim() || '';

            for (const terminal of cityTerminals) {
                if (terminal.address && terminal.address.trim().toLowerCase() === address.toLowerCase()) {
                    newSums[terminal.created_by] = (newSums[terminal.created_by] || 0) + amount;
                    pharmacyAssigned = true;
                    break;
                }
            }
        }

        // Если аптека так и не определена
        if (!pharmacyAssigned) {
            newSums.unassignedSum += amount;
        }
    });

    // Выводим детальную информацию в консоль
    if (newSums.unassignedSum > 0) {
        console.warn(' АНАЛИЗ НЕНАЗНАЧЕННЫХ ЗАКАЗОВ:');
        console.warn(`💰 Общая сумма без аптеки: ${newSums.unassignedSum.toLocaleString('ru-RU')} ₸`);
        console.warn(` Процент от общей суммы: ${((newSums.unassignedSum / newSums.total) * 100).toFixed(2)}%`);

        // Дополнительная статистика по аптекам
        const assignedPharmacies = Object.keys(newSums)
            .filter(key => key !== 'total' && key !== 'unassignedSum' && newSums[key] && newSums[key]! > 0)
            .sort((a, b) => (newSums[b]! || 0) - (newSums[a]! || 0));

        console.log('📈 Суммы по аптекам:');
        assignedPharmacies.forEach(pharmacy => {
            console.log(`  ${pharmacy}: ${(newSums[pharmacy] || 0).toLocaleString('ru-RU')} ₸`);
        });
    } else {
        console.log('Все заказы успешно привязаны к аптекам');
    }

    return newSums;
};

/**
 * Улучшенная функция сопоставления данных с поддержкой "Склад передачи КД"
 */
export const matchAndUpdateData = (
    salesData: ExcelData,
    archiveData: ExcelData,
    cityTerminals: CityTerminal[]
): {
    data: ExcelData;
    stats: {
        processed: number;
        matched: number;
        warehouseMatched: number; // Совпадения через "Склад передачи КД"
        addressMatched: number;   // Совпадения через адрес
        notMatched: number;       // Не найдено совпадений
    }
} => {
    const updatedSalesData = { ...salesData };
    const firstSalesSheet = Object.values(salesData)[0];
    const firstArchiveSheet = Object.values(archiveData)[0];

    if (!firstSalesSheet || !firstArchiveSheet) return {
        data: salesData,
        stats: { processed: 0, matched: 0, warehouseMatched: 0, addressMatched: 0, notMatched: 0 }
    };

    // Создаем расширенную Map с дополнительными данными
    const archiveOrdersMap = new Map(
        firstArchiveSheet.data.map(row => [
            row[FILE_CONFIGS.archiveOrders.mappings.orderId]?.toString(),
            {
                address: row[FILE_CONFIGS.archiveOrders.mappings.address],
                warehouseTransfer: FILE_CONFIGS.archiveOrders.mappings.warehouseTransfer
                    ? row[FILE_CONFIGS.archiveOrders.mappings.warehouseTransfer]
                    : undefined
            }
        ])
    );

    let processed = 0;
    let matched = 0;
    let warehouseMatched = 0;
    let addressMatched = 0;
    let notMatched = 0;

    const updatedData = firstSalesSheet.data.map(row => {
        processed++;
        const orderId = row[FILE_CONFIGS.salesReport.mappings.orderId]?.toString();
        const archiveData = archiveOrdersMap.get(orderId);

        if (archiveData) {
            matched++;
            const { processedAddress, terminalId, cityName, source } = processAddress(
                archiveData.address,
                archiveData.warehouseTransfer,
                cityTerminals
            );

            // Подсчет статистики по источнику сопоставления
            if (source === 'warehouse') {
                warehouseMatched++;
            } else if (source === 'address') {
                addressMatched++;
            } else {
                notMatched++;
                matched--; // Уменьшаем общий счетчик если не удалось определить город
            }

            return {
                ...row,
                [FILE_CONFIGS.salesReport.mappings.address]: processedAddress,
                [FILE_CONFIGS.salesReport.mappings.terminalId!]: terminalId || row[FILE_CONFIGS.salesReport.mappings.terminalId!]
            };
        } else {
            notMatched++;
        }
        return row;
    });

    const firstSheetName = Object.keys(salesData)[0];
    updatedSalesData[firstSheetName].data = updatedData;

    return {
        data: updatedSalesData,
        stats: {
            processed,
            matched,
            warehouseMatched,
            addressMatched,
            notMatched
        }
    };
};

export const exportToExcel = (data: ExcelData, originalFile: File) => {
    const firstSheet = Object.values(data)[0];
    if (!firstSheet) return;

    const ws = XLSX.utils.json_to_sheet(firstSheet.data, {
        header: firstSheet.headers
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    const newFileName = 'КаспийНовый.xlsx';
    XLSX.writeFile(wb, newFileName);
};

export const formatSum = (sum: number): string => {
    return new Intl.NumberFormat('ru-RU').format(sum);
};
